import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { userSchema } from "../schemas/userSchema";
import { userService } from "../../../services/userService";
import { toast } from "../../../services/toastService";

const UserForm = ({ loadData, id, setId }) => {
  const isEditMode = Boolean(id);

  const initialForm = {
    name: "",
    userName: "",
    email: "",
    password: "",
    role: "",
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(userSchema),
    context: { isEditMode },
  });

  useEffect(() => {
    if (id > 0) {
      editAsync();
    }
  }, [id]);

  const editAsync = async () => {
    try {
      const result = await userService.edit(id);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to edit User");
        return;
      }

      reset({
        name: result.value.name,
        userName: result.value.userName,
        email: result.value.email,
        password: "",
        role: result.value.roles?.[0] || "",
      });
    } catch (error) {
      toast.error("An error occurred while loading the User");
      console.log(error);
    }
  };

  const onSubmit = async (data) => {
    if (isEditMode) await updateAsync(data);
    else await addAsync(data);
  };

  const buildPayload = (data) => ({
    name: data.name,
    userName: data.userName,
    email: data.email,
    ...(data.password ? { password: data.password } : {}),
    roles: [data.role],
  });

  const addAsync = async (data) => {
    try {
      const result = await userService.create(buildPayload(data));
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to create User");
        return;
      }
      toast.success("Record added successfully");
      handleClear();
      loadData();
    } catch (error) {
      toast.error("An error occurred while creating the User");
      console.log(error);
    }
  };

  const updateAsync = async (data) => {
    try {
      const result = await userService.update(id, buildPayload(data));
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to update User");
        return;
      }
      toast.success("Record updated successfully");
      handleClear();
      loadData();
    } catch (error) {
      toast.error("An error occurred while updating the User");
      console.log(error);
    }
  };

  const handleClear = () => {
    setId(0);
    reset(initialForm);
  };

  return (
    <section className="section-user-form">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter Name"
                {...register("name")}
              />
              <small className="text-danger">{errors.name?.message}</small>
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter Username"
                {...register("userName")}
              />
              <small className="text-danger">{errors.userName?.message}</small>
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                className="form-control"
                placeholder="Enter Email"
                {...register("email")}
              />
              <small className="text-danger">{errors.email?.message}</small>
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label>Password{isEditMode ? " (leave blank to keep unchanged)" : ""}</label>
              <input
                type="password"
                className="form-control"
                placeholder="Enter Password"
                {...register("password")}
              />
              <small className="text-danger">{errors.password?.message}</small>
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label>Role</label>
              <select className="form-control" {...register("role")}>
                <option value="">Select Role</option>
                <option value="Admin">Admin</option>
                <option value="User">User</option>
              </select>
              <small className="text-danger">{errors.role?.message}</small>
            </div>
          </div>

          <div className="col-12">
            <div className="form-group flex-end">
              <button
                type="button"
                className="my-button bg-secondary mx-2"
                onClick={() => handleClear()}
              >
                Cancel
              </button>
              <button type="submit" className="my-button" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </section>
  );
};

export default UserForm;
